import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useOutletContext } from 'react-router-dom';
import { PROFILE_LIMIT, canCreateProfile, type AwsJson } from '@courseai/domain';
import type { ProjectTabContext } from '../ProjectDetail';
import { useT, type I18n, type TranslationKey } from '../../i18n';
import {
  GET_PROJECT_PROFILES,
  CREATE_VOICE_PROFILE,
  type ProjectProfilesData,
  type ProjectProfilesVariables,
  type CreateVoiceProfileData,
  type CreateVoiceProfileVariables,
  type VoiceProfileFields,
} from '../../graphql/profiles';

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

// Parse an optional AWSJSON text field. Empty/whitespace → undefined (the field
// is optional). Non-empty text must parse to a JSON object; anything else is a
// client-side validation error so we never send malformed AWSJSON. Error
// messages are localized via `t`, with the human-readable field name resolved
// from `fieldLabelKey`.
function parseOptionalJson(
  raw: string,
  t: I18n['t'],
  fieldLabelKey: TranslationKey,
): { readonly value: AwsJson | undefined } | { readonly error: string } {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return { value: undefined };
  }
  const field = t(fieldLabelKey);
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return { error: t('voiceProfiles.form.jsonInvalid', { field }) };
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return { error: t('voiceProfiles.form.jsonNotObject', { field }) };
  }
  return { value: parsed as AwsJson };
}

// Perfiles de voz tab: lists the project's Voice_Profiles (name, voiceId,
// language, and which is the default) and — for owners/editors — a create form.
// The form honors the per-project count limit (max 20, PROFILE_LIMIT) and the
// single-default rule enforced server-side (Req 10.1, 10.3, 10.5). Viewers see
// a read-only list. Identity is derived from the JWT; none is sent.
export function VoiceProfilesTab(): ReactNode {
  const { projectId, canEdit } = useOutletContext<ProjectTabContext>();
  const { t } = useT();

  const { data, loading, error, refetch } = useQuery<ProjectProfilesData, ProjectProfilesVariables>(
    GET_PROJECT_PROFILES,
    { variables: { projectId } },
  );

  const profiles = useMemo<ReadonlyArray<VoiceProfileFields>>(
    () => data?.getProject?.voiceProfiles ?? [],
    [data],
  );
  const atLimit = !canCreateProfile(profiles.length);

  if (loading && data === undefined) {
    return (
      <div className="tab-panel">
        <p>{t('voiceProfiles.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tab-panel">
        <p className="form-error">{t('voiceProfiles.loadError', { message: error.message })}</p>
        <button type="button" onClick={() => void refetch()}>
          {t('voiceProfiles.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="tab-panel">
      <div className="tab-panel-header">
        <h3>{t('voiceProfiles.title')}</h3>
        <span className="profile-count">
          {profiles.length} / {PROFILE_LIMIT}
        </span>
      </div>

      {canEdit && (
        <VoiceProfileForm
          projectId={projectId}
          atLimit={atLimit}
          hasProfiles={profiles.length > 0}
          onCreated={async () => {
            await refetch();
          }}
        />
      )}

      {profiles.length === 0 ? (
        <p className="tab-placeholder">{t('voiceProfiles.empty')}</p>
      ) : (
        <ul className="profile-list">
          {profiles.map((profile) => (
            <li key={profile.voiceProfileId} className="profile-card">
              <div className="profile-card-header">
                <span className="profile-card-name">{profile.name}</span>
                {profile.isDefault && (
                  <span className="profile-default-badge">{t('voiceProfiles.default')}</span>
                )}
              </div>
              <dl className="profile-card-fields">
                <div>
                  <dt>{t('voiceProfiles.field.voice')}</dt>
                  <dd>{profile.voiceId}</dd>
                </div>
                <div>
                  <dt>{t('voiceProfiles.field.language')}</dt>
                  <dd>{profile.language}</dd>
                </div>
              </dl>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface VoiceProfileFormProps {
  projectId: string;
  atLimit: boolean;
  hasProfiles: boolean;
  onCreated(): Promise<void> | void;
}

// Owner/editor-only form to create a Voice_Profile. Requires name, voiceId, and
// language; voiceSettings and pausePattern are optional AWSJSON objects. The
// "predeterminado" checkbox designates the new profile as the single project
// default (Req 10.5). Creation is disabled at the count limit (Req 10.1).
function VoiceProfileForm({
  projectId,
  atLimit,
  hasProfiles,
  onCreated,
}: VoiceProfileFormProps): ReactNode {
  const { t } = useT();
  const [name, setName] = useState('');
  const [voiceId, setVoiceId] = useState('');
  const [language, setLanguage] = useState('es');
  const [voiceSettings, setVoiceSettings] = useState('');
  const [pausePattern, setPausePattern] = useState('');
  // Default to true when this would be the project's first profile: with a
  // single profile it is the effective default anyway (Req 10.5).
  const [isDefault, setIsDefault] = useState(!hasProfiles);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);

  const [createProfile, { loading }] = useMutation<
    CreateVoiceProfileData,
    CreateVoiceProfileVariables
  >(CREATE_VOICE_PROFILE);

  function resetForm(): void {
    setName('');
    setVoiceId('');
    setLanguage('es');
    setVoiceSettings('');
    setPausePattern('');
    setIsDefault(false);
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSubmitError(undefined);

    if (atLimit) {
      setSubmitError(t('voiceProfiles.form.limitReached', { limit: PROFILE_LIMIT }));
      return;
    }

    const trimmedName = name.trim();
    const trimmedVoiceId = voiceId.trim();
    const trimmedLanguage = language.trim();
    if (trimmedName.length === 0 || trimmedVoiceId.length === 0 || trimmedLanguage.length === 0) {
      setSubmitError(t('voiceProfiles.form.requiredFields'));
      return;
    }

    const settings = parseOptionalJson(voiceSettings, t, 'voiceProfiles.form.voiceSettingsLabel');
    if ('error' in settings) {
      setSubmitError(settings.error);
      return;
    }
    const pauses = parseOptionalJson(pausePattern, t, 'voiceProfiles.form.pausePatternLabel');
    if ('error' in pauses) {
      setSubmitError(pauses.error);
      return;
    }

    try {
      await createProfile({
        variables: {
          input: {
            projectId,
            name: trimmedName,
            voiceId: trimmedVoiceId,
            language: trimmedLanguage,
            voiceSettings: settings.value,
            pausePattern: pauses.value,
            isDefault,
          },
        },
      });
      resetForm();
      await onCreated();
    } catch (err) {
      setSubmitError(errorMessage(err, t('voiceProfiles.form.createError')));
    }
  }

  return (
    <form className="profile-form" onSubmit={handleSubmit}>
      <div className="profile-form-grid">
        <label>
          {t('voiceProfiles.form.name')}
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('voiceProfiles.form.namePlaceholder')}
            required
          />
        </label>
        <label>
          {t('voiceProfiles.form.voiceId')}
          <input
            type="text"
            value={voiceId}
            onChange={(e) => setVoiceId(e.target.value)}
            placeholder={t('voiceProfiles.form.voiceIdPlaceholder')}
            required
          />
        </label>
        <label>
          {t('voiceProfiles.form.language')}
          <input
            type="text"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            placeholder={t('voiceProfiles.form.languagePlaceholder')}
            required
          />
        </label>
      </div>
      <label>
        {t('voiceProfiles.form.voiceSettings')}
        <textarea
          value={voiceSettings}
          onChange={(e) => setVoiceSettings(e.target.value)}
          placeholder='{ "stability": 0.5 }'
          rows={2}
        />
      </label>
      <label>
        {t('voiceProfiles.form.pausePattern')}
        <textarea
          value={pausePattern}
          onChange={(e) => setPausePattern(e.target.value)}
          placeholder='{ "betweenSegmentsSeconds": 2 }'
          rows={2}
        />
      </label>
      <label className="profile-form-default">
        <input
          type="checkbox"
          checked={isDefault}
          onChange={(e) => setIsDefault(e.target.checked)}
        />
        {t('voiceProfiles.form.default')}
      </label>
      <div className="profile-form-actions">
        <button type="submit" disabled={loading || atLimit}>
          {loading ? t('voiceProfiles.form.creating') : t('voiceProfiles.form.create')}
        </button>
      </div>
      {atLimit && (
        <p className="field-hint">{t('voiceProfiles.form.limitHint', { limit: PROFILE_LIMIT })}</p>
      )}
      {submitError && <p className="form-error">{submitError}</p>}
    </form>
  );
}
